import { Minus, Plus, RotateCcw, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { useCallback, useEffect, useRef, useState } from "react";

import { useIslandContainer } from "@/contexts/island-container";
import { useLanguage } from "@/contexts/language";
import { cn } from "@/lib/utils";

// Adapted from the reference: framer-motion (not in this project's deps)
// replaced with plain CSS transforms + pointer events. Same public API:
// zoom via wheel / double-click / buttons, pan via drag, pinch on touch.

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const WHEEL_STEP = 1.12;
const BUTTON_STEP = 1.4;

function clampScale(value: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
}

interface Transform {
  x: number;
  y: number;
  scale: number;
}

const IDENTITY: Transform = { x: 0, y: 0, scale: 1 };

interface ImageLightboxProps {
  open: boolean;
  onClose: () => void;
  src: string;
  alt?: string;
}

export function ImageLightbox({ open, onClose, src, alt }: ImageLightboxProps) {
  const { t } = useLanguage();
  const { containerRef } = useIslandContainer();
  const stageRef = useRef<HTMLDivElement>(null);
  const draggedRef = useRef(false);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchStart = useRef<{ dist: number; scale: number } | null>(null);
  const dragStart = useRef<{
    x: number;
    y: number;
    tx: number;
    ty: number;
  } | null>(null);

  const [transform, setTransform] = useState<Transform>(IDENTITY);
  const [smooth, setSmooth] = useState(false);

  const reset = useCallback((animated: boolean) => {
    setSmooth(animated);
    setTransform(IDENTITY);
  }, []);

  const setScaleTo = useCallback((next: number, animated: boolean) => {
    const clamped = clampScale(next);
    setSmooth(animated);
    setTransform((prev) =>
      clamped === MIN_SCALE ? IDENTITY : { ...prev, scale: clamped },
    );
  }, []);

  useEffect(() => {
    if (!open) reset(false);
  }, [open, reset]);

  useEffect(() => {
    reset(false);
  }, [src, reset]);

  useEffect(() => {
    if (!open) return;
    const stage = stageRef.current;
    if (!stage) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      setTransform((prev) => {
        const scale = clampScale(prev.scale * factor);
        return scale === MIN_SCALE ? IDENTITY : { ...prev, scale };
      });
      setSmooth(false);
    };
    stage.addEventListener("wheel", onWheel, { passive: false });
    return () => stage.removeEventListener("wheel", onWheel);
  }, [open]);

  const handleStageClick = useCallback(() => {
    if (draggedRef.current) {
      draggedRef.current = false;
      return;
    }
    onClose();
  }, [onClose]);

  const handleDoubleClick = useCallback(() => {
    setScaleTo(
      transform.scale > MIN_SCALE ? MIN_SCALE : DOUBLE_TAP_SCALE,
      true,
    );
  }, [transform.scale, setScaleTo]);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 2) {
        const [a, b] = [...pointers.current.values()];
        pinchStart.current = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          scale: transform.scale,
        };
        dragStart.current = null;
      } else if (transform.scale > MIN_SCALE) {
        dragStart.current = {
          x: e.clientX,
          y: e.clientY,
          tx: transform.x,
          ty: transform.y,
        };
      }
    },
    [transform],
  );

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinchStart.current) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const ratio = dist / pinchStart.current.dist;
      const scale = clampScale(pinchStart.current.scale * ratio);
      setSmooth(false);
      setTransform((prev) =>
        scale === MIN_SCALE ? IDENTITY : { ...prev, scale },
      );
      return;
    }
    if (dragStart.current) {
      const start = dragStart.current;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      if (Math.abs(dx) + Math.abs(dy) > 3) draggedRef.current = true;
      setSmooth(false);
      setTransform((prev) => ({
        ...prev,
        x: start.tx + dx,
        y: start.ty + dy,
      }));
    }
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      pinchStart.current = null;
    }
    if (pointers.current.size === 0) {
      dragStart.current = null;
    }
  }, []);

  const zoomed = transform.scale > MIN_SCALE;

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => !next && onClose()}
    >
      <DialogPrimitive.Portal container={containerRef.current}>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          style={{ touchAction: "none" }}
          className="tw:fixed tw:inset-0 tw:z-[9999] tw:flex tw:items-center tw:justify-center tw:bg-black/90 tw:backdrop-blur-xl tw:outline-hidden tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:animate-in tw:data-[state=open]:fade-in-0"
        >
          <DialogPrimitive.Title className="tw:sr-only">
            {alt || t("Common:Close")}
          </DialogPrimitive.Title>

          <DialogPrimitive.Close
            onClick={(e) => e.stopPropagation()}
            aria-label={t("Common:Close")}
            className="tw:absolute tw:end-4 tw:top-4 tw:z-10 tw:flex tw:size-10 tw:items-center tw:justify-center tw:rounded-full tw:bg-white/10 tw:text-white tw:backdrop-blur-md tw:transition-colors tw:hover:bg-white/20"
          >
            <X className="tw:size-5" />
          </DialogPrimitive.Close>

          <div
            onClick={(e) => e.stopPropagation()}
            className="tw:absolute tw:bottom-6 tw:left-1/2 tw:z-10 tw:flex tw:-translate-x-1/2 tw:items-center tw:gap-1 tw:rounded-full tw:bg-white/10 tw:px-1.5 tw:py-1 tw:text-white tw:backdrop-blur-md"
          >
            <button
              type="button"
              onClick={() => setScaleTo(transform.scale / BUTTON_STEP, true)}
              disabled={!zoomed}
              aria-label={t("Common:ZoomOut")}
              className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-white/20 tw:disabled:opacity-40 tw:disabled:hover:bg-transparent"
            >
              <Minus className="tw:size-4" />
            </button>
            <span className="tw:w-12 tw:text-center tw:font-mono tw:text-xs tw:tabular-nums">
              {Math.round(transform.scale * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setScaleTo(transform.scale * BUTTON_STEP, true)}
              disabled={transform.scale >= MAX_SCALE}
              aria-label={t("Common:ZoomIn")}
              className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-white/20 tw:disabled:opacity-40 tw:disabled:hover:bg-transparent"
            >
              <Plus className="tw:size-4" />
            </button>
            <button
              type="button"
              onClick={() => reset(true)}
              disabled={!zoomed}
              aria-label={t("Common:ResetZoom")}
              className="tw:flex tw:size-8 tw:items-center tw:justify-center tw:rounded-full tw:transition-colors tw:hover:bg-white/20 tw:disabled:opacity-40 tw:disabled:hover:bg-transparent"
            >
              <RotateCcw className="tw:size-4" />
            </button>
          </div>

          <div
            ref={stageRef}
            onClick={handleStageClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            className="tw:relative tw:flex tw:size-full tw:items-center tw:justify-center tw:overflow-hidden tw:p-4 tw:sm:p-12"
          >
            <img
              src={src}
              alt={alt ?? ""}
              draggable={false}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={handleDoubleClick}
              style={{
                transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                transition: smooth
                  ? "transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)"
                  : undefined,
              }}
              className={cn(
                "tw:max-h-full tw:max-w-full tw:rounded-lg tw:object-contain tw:shadow-2xl tw:select-none",
                zoomed
                  ? "tw:cursor-grab tw:active:cursor-grabbing"
                  : "tw:cursor-zoom-in",
              )}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
